import apiClient from './apiClient';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * Invoice / Billing Profile API — org invoice_config (T-INV1)
 *
 * Endpoints:
 *   GET /api/organisation/invoice-config — Get invoice/billing profile
 *   PUT /api/organisation/invoice-config — Update invoice/billing profile
 *
 * CONTRACT NOTE: PUT /invoice-config + its `{ config, tax_id, message }`
 * response are FROZEN (state/epics/IPD-MVP/round2-invoice-ux/BUILD-PLAN.md
 * §INV-1 — corrects the story's earlier "PATCH /organisation/profile"
 * sketch). The GET route/shape here is ASSUMED to mirror the PUT path and
 * response envelope (same GET+PUT pairing as /prescription-config) — the
 * BUILD-PLAN only froze "profile GET echoes invoice_config" without naming
 * a concrete route, and no working GET /organisation/profile exists in the
 * backend today. Reconcile against the real T-INV1-BE route at integration.
 * ═══════════════════════════════════════════════════════════════════════
 */

/**
 * Get the current invoice/billing profile for the org.
 * @returns {Promise<{ config: Object, tax_id: string|null }>}
 */
export const getInvoiceConfig = async () => {
  const response = await apiClient.get('/api/organisation/invoice-config');
  return response.data;
};

/**
 * Update the invoice/billing profile.
 * @param {Object} payload - flat body: legal_name, registered_address, contact,
 *   logo_s3_key, footer_terms, pan, authorised_signatory_label, gstin, and the
 *   Round-3/S7 statutory fields (authorised_signatory_name,
 *   signatory_designation, place_of_supply, sac_code, declaration,
 *   eoe_enabled — bound verbatim to InvoiceConfigUpdate). The backend
 *   write-through strips `gstin` out of invoice_config and stamps the org
 *   `tax_id` (one source of truth). Body is extra=forbid — send `gstin`,
 *   NOT `tax_id` (reconcile D).
 * @returns {Promise<{ config: Object, tax_id: string, message: string }>}
 */
export const updateInvoiceConfig = async (payload) => {
  const response = await apiClient.put('/api/organisation/invoice-config', payload);
  return response.data;
};
